/* eslint-disable sonarjs/no-duplicate-string */

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    EventEmitter,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { ClareServicePlan, ClareServiceSubscription, SecuritySystem, TemporaryProjectFilter } from "@app/project/model";
import { ProjectView, ProjectViewHelper } from "@app/project/model/project-view";
import { SERVICE_PLAN_TIERS, ServicePlanTiers } from "@app/project/model/service-plan";
import { LockoutTimeCount, OccupancyCount, ServicePlanCount, StatusCount } from "@app/project/store/projects.state";
import { ClareDialogComponent, IDialogConfig } from "@app/shared/components/clare-dialog/clare-dialog.component";
import { IClareTableComponent, TableAction, TableActionDisplay, TableColumns } from "@app/shared/model/action.model";
import { NgxPermissionsObject, NgxPermissionsService } from "ngx-permissions";
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, of, pipe } from "rxjs";
import { delay, filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from "rxjs/operators";

import { MatDialog } from "@angular/material/dialog";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { Params } from "@angular/router";
import { ADD_ACCOUNT } from "@app/app-routing.constants";
import { AuthUser } from "@app/auth/model/auth-user";
import { AuthUserService } from "@app/auth/services/auth-user.service";
import { CellularService } from "@app/cellular/services/cellular.service";
import { Entity } from "@app/entity/model/entity";
import { EntityService } from "@app/entity/services/entity.service";
import { Project } from "@app/project/model/project";
import { ProjectListItem } from "@app/project/model/project-list-item";
import { CHServiceService } from "@app/project/services/ch-services.service";
import { ProjectDbService } from "@app/project/services/project-db.service";
import { ProjectStatusService } from "@app/project/services/project-status.service";
import { ProjectService } from "@app/project/services/project.service";
import { ServicePlansService } from "@app/project/services/service-plans.service";
import { ProjectActions } from "@app/project/store/actions";
import { ProjectViewDispatchers } from "@app/project/store/dispatchers/project-view.dispatchers";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { RouterActions } from "@app/root-store";
import { SnackbarActions } from "@app/root-store/actions";
import { RouterService } from "@app/root-store/router.service";
import { BreakpointService } from "@app/shared/services/breakpoint.service";
import { IFilters } from "@app/shared/services/filter.service";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { StatusCountService } from "@app/shared/services/status-count.service";
import { TablePropertiesService } from "@app/shared/services/table-properties.service";
import { NgxPermission } from "ngx-permissions/lib/model/permission.model";
import { TableColumnDef } from "../../../shared/model/action.model";
import { ProjectHelperService } from "../../services/project-helper.service";
import { ConfirmationAction } from "../../store/actions/generic.action";
import { ExportCSVComponent } from "../export-csv/export-csv.component";
import { ProjectViewsContainerComponent } from "../project-views/project-views-container/project-views-container.component";
import { ProjectStatus } from "@app/project/store/selectors/projects.selectors";

export enum AccountStatusMessages {
    activationConfirmationMsg = "Are you sure you want to activate this account?",
    checkingActivationStatusMsg = "Checking service activation statuses",
    activationWarning = "Please deactivate _s_ services prior to suspending this account.",
    deactivateSpWarning = "Please deactivate _s_ services prior to updating the controller.",
    suspendConfirmationMsg = `Are you sure you want to suspend this account?
    <br />
    <br />
    Note: Suspending the account will set the service plan to Basic, deactivating cellular and monitoring.`,
}

enum RouteParams {
    query = "query",
    servicePlans = "service-plans",
    columns = "columns",
    status = "status",
}

@Component({
    selector: "app-list-projects",
    templateUrl: "./list-projects.component.html",
    styleUrls: ["./list-projects.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListProjectsComponent implements OnInit, OnDestroy, IClareTableComponent<Project> {
    public isMobileView$: Observable<boolean>;
    public dataSource$: Observable<ProjectListItem[] | Error>;
    public columns$: Observable<TableColumns>;

    public callState$: Observable<any>;
    entities$: Observable<any> = of([]);
    selectedEntityId$: Observable<number>;
    selectedEntity$: Observable<Entity>;
    projectFilter: TemporaryProjectFilter = {};
    userDisplayName = "";
    userCompanyName = "";

    // Filter buttons in list header
    public onlineStatuses: StatusCount[] = [
        { key: "online", display: "Online", value: 0 },
        { key: "offline", display: "Offline", value: 0 },
        { key: "unconfigured", display: "Unconfigured", value: 0 },
    ];
    public activeCounts: StatusCount[] = [
        { key: "active", display: "Active", value: 0 },
        { key: "suspended", display: "Suspended", value: 0 },
    ];

    public servicePlanCounts: ServicePlanCount[] = [
        { key: "Basic", display: "Basic", value: 0 },
        {
            key: "Interactive",
            display: "Interactive",
            value: 0,
        },
        {
            key: "Interactive Plus",
            display: "Interactive Plus",
            value: 0,
        },
        {
            key: "Interactive Plus LTE",
            display: "Interactive Plus LTE",
            value: 0,
        },
    ];

    public occupancyCounts: OccupancyCount[] = [
        { key: "isOccupied", display: "Occupied", value: 0 },
        { key: "unOccupied", display: "Vacant", value: 0 },
    ];

    public lockoutTimeCount: LockoutTimeCount = { key: "lockoutTime", display: "Enabled", value: 0 };

    public projectViewConfig$: Observable<any>;
    public exportCSVFactoryConfig$ = new BehaviorSubject({
        factory: this.componentFactoryResolver.resolveComponentFactory(ExportCSVComponent),
        config: {
            columns: {},
            rows: [],
            dataType: "Accounts",
        },
    });

    public tableProperties$ = TablePropertiesService.getTableFilterProperties$(null);
    public tableActionOptions = TablePropertiesService.getActionOptions(this.getTableActions(), { display: TableActionDisplay.dynamicBar });
    private readonly lockoutCountBehaviorSubject$ = new BehaviorSubject<number>(0);

    updateTableProperties(updatedProperties: Partial<TemporaryProjectFilter>) {
        this.persistCurrentProjectFilter(updatedProperties);
        return TablePropertiesService.updateTableProperties(this.tableProperties$, updatedProperties);
    }

    private persistCurrentProjectFilter(updatedProperties: Partial<TemporaryProjectFilter>) {
        const { searchTerm } = updatedProperties;

        if (updatedProperties?.hasOwnProperty("searchTerm")) {
            this.projectViewDispatcher.persistViewLocal({ searchTerm });
        }

        if (updatedProperties?.hasOwnProperty("selectedFilters")) {
            const selectedFilters = { ...this.tableProperties$.value.selectedFilters, ...updatedProperties.selectedFilters };
            this.projectViewDispatcher.persistViewLocal({ selectedFilters });
        }
    }

    pageSize = 200;
    totalAccount = 0;
    private unsubscribeFromDialog$ = new Subject();
    private closeSaveDialog: EventEmitter<any> = new EventEmitter();
    private mobileColumns = {
        name: "Name/UUID",
        address: "Address",
        _listStructure: [
            ["name", "uuid"],
            ["street", "street2"],
        ],
    };
    private unsubscribe$ = new Subject();
    private deleteActivatedProjectPermission: NgxPermission;
    private suspendActivatedProjectPermission: NgxPermission;

    private subscriptions: Subscription[] = [];

    constructor(
        private readonly authUserService: AuthUserService,
        private readonly projectStatusService: ProjectStatusService,
        private readonly statusCountService: StatusCountService,
        private readonly projectService: ProjectService,
        private readonly servicePlanService: ServicePlansService,
        private readonly dialog: MatDialog,
        private readonly projectViewDispatcher: ProjectViewDispatchers,
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly projectDispatcher: ProjectDispatchers,
        private readonly permissionsService: NgxPermissionsService,
        private readonly breakpointService: BreakpointService,
        private readonly cellularService: CellularService,
        private readonly chServiceService: CHServiceService,
        private readonly projectDbService: ProjectDbService,
        private readonly entityService: EntityService,
        private readonly routerService: RouterService,
        private readonly cdr: ChangeDetectorRef,
        matIconRegistry: MatIconRegistry,
        domSanitizer: DomSanitizer
    ) {
        matIconRegistry.addSvgIcon(`exclamation`, domSanitizer.bypassSecurityTrustResourceUrl(`assets/images/exclamation.svg`));
    }

    public onFilterChange(selectedFilters: IFilters) {
        this.updateTableProperties({ selectedFilters });
    }

    resetFilters() {
        const currentFilter = this.tableProperties$.value;
        this.tableProperties$.next({
            ...currentFilter,
            selectedFilters: {},
        });
        this.projectViewDispatcher.persistViewLocal({ selectedFilters: {} });
        this.projectFilter = {
            selectedFilters: {
                onlineStatus: [],
                inactive: [],
                monitoredStatus: [],
                isOccupied: [],
                lockoutTime: [],
            },
        };
    }

    /**
     * @description creates a dynamic permission string based on the actionItem input
     * @param {string} actionItem
     * @returns string
     */
    public checkPermissions(actionItem: string) {
        return actionItem === "Devices";
    }

    addProject(event) {
        if (event) {
            this.projectDispatcher.dispatch(RouterActions.routerGo({ path: [`/${ADD_ACCOUNT}`] }));
        }
    }

    editProject(id: number) {
        if (id) {
            this.projectDispatcher.dispatch(RouterActions.routerGo({ path: ["/accounts/edit", id, "summary"] }));
        }
    }

    deleteActivatedProject(project: Project) {
        const confirmation: ConfirmationAction = {
            confirmValue: ProjectHelperService.getProjectUuid(project),
            confirmLabel: "UUID",
        };
        this.projectService.deleteProjectConfirmation({ project, closeDialog: null, confirmation });
    }

    deleteProject(project: Project) {
        this.projectService.deleteProjectConfirmation({ project });
    }

    refresh() {
        this.projectDispatcher.clearProjectArray();
        combineLatest([
            this.getAuthUser(),
            this.projectViewDispatcher.getActiveProjectView$().pipe(filter(activeView => !!activeView)),
            this.selectedEntityId$,
        ])
            .pipe(
                tap(([authUser, activeView, entityId]) => {
                    this.dispatchProjects(authUser, activeView, entityId);
                    this.projectDispatcher.dispatch(
                        SnackbarActions.openSnackbar({
                            message: new HttpResponseHandler(null, null, "Retrieving fresh data from the server."),
                        })
                    );
                })
            )
            .subscribe()
            .unsubscribe(); // unsubscribing immediately after fetching data
    }

    private getAuthUser() {
        return this.authUserService.selectAuthUser$().pipe(
            filter(user => !!user),
            take(1),
            tap(user => {
                this.userDisplayName = user.displayName || user.firstName + " " + user.lastName;
                this.userCompanyName = user["entityName"];
            })
        );
    }

    dispatchProjects(authUser: AuthUser, projectView?: ProjectView, entityId?: number) {
        this.projectDispatcher.dispatch(new ProjectActions.GetProjects({ entityId: entityId || authUser.entityId, projectView }));
    }

    private loadProjectServicePlans() {
        let servicePlanSummaryObs$: Observable<Record<string, number>>;

        if (this.isAbleToSuspendAccount) {
            servicePlanSummaryObs$ = this.projectStatusService.selectProjectServicePlansCounts$();
        } else {
            servicePlanSummaryObs$ = this.projectStatusService.selectActiveProjectServicePlansCounts$();
        }

        return pipe(
            switchMap(_ => servicePlanSummaryObs$),
            tap(countingData => {
                this.servicePlanCounts.forEach(sp => {
                    sp.value = countingData[sp.key] || 0;
                });
                this.servicePlanCounts = [...this.servicePlanCounts];
            })
        );
    }

    private loadProjectOccupancy() {
        let occupancySummaryObs$: Observable<Record<string, number>>;

        if (this.isAbleToSuspendAccount) {
            occupancySummaryObs$ = this.projectStatusService.selectProjectOccupancyCounts$();
        } else {
            occupancySummaryObs$ = this.projectStatusService.selectActiveProjectOccupancyCounts$();
        }

        return pipe(
            switchMap(_ => occupancySummaryObs$),
            tap(countingData => {
                this.occupancyCounts.forEach(sp => {
                    sp.value = countingData[sp.key] || 0;
                });
                this.occupancyCounts = [...this.occupancyCounts];
            })
        );
    }

    private loadProjectStatuses() {
        let projectStatusCountsObs$: Observable<Record<ProjectStatus, number>>;

        if (this.isAbleToSuspendAccount) {
            projectStatusCountsObs$ = this.projectStatusService.selectProjectStatusCounts$();
        } else {
            projectStatusCountsObs$ = this.projectStatusService.selectActiveProjectStatusCounts$();
        }

        return pipe(
            switchMap(_ => this.projectStatusService.getProjectStatuses$()),
            switchMap(_ => projectStatusCountsObs$),
            map(statuses => this.statusCountService.getStatusCounts(statuses)),
            tap(({ statusItems }) => {
                this.onlineStatuses.forEach(statusItem => (statusItem.value = 0));
                statusItems.forEach(v => {
                    const status = this.onlineStatuses.find(statusItem => statusItem.key === v.label);
                    status.value = v.itemCount;
                });
                this.onlineStatuses = [...this.onlineStatuses];
            })
        );
    }

    private loadProjectActives() {
        return pipe(
            switchMap(_ => this.projectStatusService.selectProjectActiveCounts$()),
            tap(({ active, suspended }) => {
                this.activeCounts.forEach(status => {
                    status.value = status.key === "active" ? active : suspended;
                    return status;
                });
                this.activeCounts = [...this.activeCounts];
            })
        );
    }

    private loadProjectLockoutCount() {
        let lockoutCountObs$: Observable<number>;

        if (this.isAbleToSuspendAccount) {
            lockoutCountObs$ = this.projectStatusService.selectProjectLockoutCount$();
        } else {
            lockoutCountObs$ = this.projectStatusService.selectActiveProjectLockoutCount$();
        }

        return pipe(
            switchMap(() => lockoutCountObs$),
            tap(lockoutCount => {
                this.lockoutTimeCount.value = lockoutCount;
                this.lockoutTimeCount = { ...this.lockoutTimeCount };
                this.lockoutCountBehaviorSubject$.next(lockoutCount);
            })
        );
    }

    onSelectSubdealer($event) {
        this.projectDispatcher.setSelectedEntity($event?.id);
    }

    ngOnInit() {
        this.getUserPermissions();
        this.selectedEntityId$ = this.projectDispatcher.getSelectedEntity();
        this.entities$ = this.projectDispatcher.getDealers();
        this.isMobileView$ = this.breakpointService.isMobileView$();

        this.callState$ = this.projectService.getProjectCallState$();

        // In order to inject project list view to the table header, we need to wait a little while
        // for the table template to render first before passing the injecting content
        this.projectViewConfig$ = this.getProjectViewConfig$().pipe(take(1), delay(200));

        this.getTotalAccounts();
        // Set initial filters
        this.routerService
            .getQueryParams()
            .pipe(
                takeUntil(this.unsubscribe$),
                tap(params => this.initProjectFilter(params))
            )
            .subscribe();

        this.dataSource$ = this.getProjectList$();
        this.columns$ = this.getAuthUser().pipe(
            switchMap(authUser =>
                combineLatest([
                    this.projectViewDispatcher.getSimplifiedColumns(authUser),
                    this.isMobileView$,
                    this.lockoutCountBehaviorSubject$,
                ]).pipe(
                    map(([columns, isMobileView, lockoutCount]) => {
                        // dynamically add lockout column if any account is locked out
                        if (lockoutCount > 0) {
                            columns = { ...columns, lockoutTime: { displayName: "Lockout Screen", dataSortType: "datetime" } };
                        }
                        this.updateTableProperties({ columnsToSearch: Object.keys(columns) });
                        if (!isMobileView) {
                            const displayColumns = {};
                            Object.entries(columns).forEach(([key, value]: [string, string | TableColumnDef]) => {
                                displayColumns[key] = !!value && typeof value === "object" ? value.displayName : value;
                            });
                            this.exportCSVFactoryConfig$.value.config.columns = displayColumns;
                            return columns;
                        }

                        return this.mobileColumns;
                    })
                )
            )
        );

        this.selectedEntity$ = combineLatest([this.entities$, this.selectedEntityId$]).pipe(
            takeUntil(this.unsubscribe$),
            map(([entities, entityId]) => entities.find(entity => entity.id === entityId))
        );

        combineLatest([
            this.getAuthUser(),
            this.projectViewDispatcher.getActiveProjectView$().pipe(filter(activeView => !!activeView)),
            this.selectedEntityId$,
            this.servicePlanService.getServicePlansObj$(),
        ])
            .pipe(
                switchMap(([authUser, activeView, entityId]) => {
                    this.dispatchProjects(authUser, activeView, entityId);
                    return this.projectService.getProjectsUUIDs(entityId).pipe(
                        this.loadProjectStatuses(),
                        this.loadProjectActives(),
                        this.loadProjectServicePlans(),
                        this.loadProjectOccupancy(),
                        this.loadProjectLockoutCount(),
                        map(_ => entityId)
                    );
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        if (window.localStorage?.getItem("cachePageSize")) {
            this.pageSize = +window.localStorage.getItem("cachePageSize");
        }
    }

    private initProjectFilter(params: Params) {
        // Filters from sessionStorage
        const sessionFilter = this.projectViewDispatcher.getCurrentProjectFilter();

        // Filters from URL
        // Init with saved view but ignore any saved filters
        // (because someone visiting /accounts?service-plans=interactive expects to see *all* the Interactive plans)
        const urlFilter: Partial<TemporaryProjectFilter> = { view: sessionFilter.view, selectedFilters: {}, searchTerm: "" };
        const paramsUpdate = { ...params };
        let useUrlFilter = false;

        if (paramsUpdate[RouteParams.query]) {
            urlFilter.searchTerm = decodeURIComponent(paramsUpdate[RouteParams.query]);
            useUrlFilter = true;

            delete paramsUpdate[RouteParams.query];
        }

        if (paramsUpdate[RouteParams.columns]) {
            // @TODO: Handle searches restricted to certain columns in redesign
            //        We have logic for this (ProjectFilter.columnsToSearch), but no way to view / configure this in the UI

            delete paramsUpdate[RouteParams.columns];
        }

        if (paramsUpdate[RouteParams.status]) {
            const parsed = decodeURIComponent(paramsUpdate[RouteParams.status]).toLocaleLowerCase().split(",");
            const whitelist = this.onlineStatuses.map(d => d.key);

            const [filtered, invalid] = this.filterByWhitelist(parsed, whitelist);
            if (filtered.length) {
                urlFilter.selectedFilters.onlineStatus = filtered;
                useUrlFilter = true;
            }
            if (invalid.length) {
                console.warn(`Invalid values for "status":`, invalid);
            }

            delete paramsUpdate[RouteParams.status];
        }

        if (paramsUpdate[RouteParams.servicePlans]) {
            const parsed = decodeURIComponent(paramsUpdate[RouteParams.servicePlans]).toLocaleLowerCase().split(",");
            const whitelist = SERVICE_PLAN_TIERS.map(name => name.toLocaleLowerCase());

            const [filtered, invalid] = this.filterByWhitelist(parsed, whitelist);
            if (filtered.length) {
                // @TODO: Uncomment this when page is redesigned to include Service Plan filters
                // urlFilter.selectedFilters.servicePlans = filtered;
                useUrlFilter = true;
            }
            if (invalid.length) {
                console.warn(`Invalid values for "service-plans":`, invalid);
            }

            delete paramsUpdate[RouteParams.servicePlans];
        }

        // URL filters have precedence over session storage
        if (useUrlFilter) {
            this.projectFilter = urlFilter;
            this.persistCurrentProjectFilter(urlFilter);
        } else {
            this.projectFilter = sessionFilter;
        }

        // Since these params aren't updated after user input, remove them from url
        const hasParamChanges = Object.keys(params).length !== Object.keys(paramsUpdate).length;
        if (hasParamChanges) {
            this.routerService.updateQueryParams(paramsUpdate).subscribe();
        }
        this.cdr.detectChanges();
    }

    getProjectViewConfig$() {
        const viewConfig = {
            factory: this.componentFactoryResolver.resolveComponentFactory(ProjectViewsContainerComponent),
            config: {},
        };

        return this.getAuthUser().pipe(
            switchMap(authUser =>
                this.projectViewDispatcher.getProjectViews$(authUser.entityId, authUser.id).pipe(
                    withLatestFrom(this.projectViewDispatcher.getActiveProjectView$()),
                    filter((_, activeView) => !activeView),
                    tap(([projectViews, activeView]) => {
                        const projectView = this.getProjectView(projectViews, activeView, authUser);

                        // factoryConfig is sent to the clare table so we can set properties on the projectview viewchild container.
                        // We only want to set this once, otherwise multiple projectview select components will be created.
                        this.projectViewDispatcher.dispatchUpdateProjectViewSuccessAction(projectView);
                    }),
                    map(() => {
                        // if the active view isn't set, which should only be the first time this is hit
                        // set up the config items for the ProjectViewsContainerComponent
                        viewConfig.config = {
                            userId: authUser.id,
                            entityId: authUser.entityId,
                            authUser,
                        };

                        return viewConfig;
                    })
                )
            )
        );
    }

    getProjectView(projectViews: ProjectView[], activeView: ProjectView, authUser: AuthUser): ProjectView {
        if (activeView) {
            return activeView;
        }

        let userDefaultView = projectViews?.find(view => view && view.id === this.projectFilter.view);
        if (!userDefaultView) {
            // find the default view for the current user
            userDefaultView = projectViews?.find(view => view?.isDefault && view?.userinfoId === authUser.id);
        }
        if (userDefaultView?.viewData) {
            // if a default projectview already exists for the user, return it
            return userDefaultView;
        }

        return ProjectViewHelper.getDefaultViewForRole(authUser);
    }

    get isAbleToDeleteActiveProject(): boolean {
        return !!this.deleteActivatedProjectPermission;
    }

    get isAbleToSuspendAccount(): boolean {
        return !!this.suspendActivatedProjectPermission;
    }

    private getProjectList$() {
        return this.projectService.getProjectList$().pipe(
            switchMap((data: any[]) => this.tableProperties$.pipe(map(props => TablePropertiesService.searchData(data, props)))),
            tap(data => (this.exportCSVFactoryConfig$.value.config.rows = data))
        );
    }
    private getTotalAccounts() {
        return this.projectService
            .getProjectList$()
            .pipe(
                takeUntil(this.unsubscribe$),
                map(data => {
                    this.totalAccount = data.length;
                })
            )
            .subscribe();
    }
    unsubscribeFromDialog() {
        this.unsubscribeFromDialog$.next(null);
        this.unsubscribeFromDialog$.complete();
    }

    onCancelModal() {
        this.dialog.closeAll();
    }

    onActivateOrSuspend(project: ProjectListItem) {
        this.unsubscribeFromDialog();
        this.closeSaveDialog.pipe(take(1)).subscribe(() => {
            this.servicePlanService.clearSubscriptions();
            this.projectService.clearAllDCOs();
            this.onCancelModal();
        });
        const doActivate = project.dbData.inactive;
        this.projectDispatcher.activateOrSuspendProject(doActivate, project.dbData.id, this.closeSaveDialog);
    }

    private setupActivateDialog(emitter: EventEmitter<void>): IDialogConfig {
        return {
            id: "activate-account",
            data: {
                title: "Activate Account",
                message: AccountStatusMessages.activationConfirmationMsg,
                loading: false,
                icon: "exclamation",
                isCloseShown: true,
                buttons: [
                    {
                        text: "Activate",
                        buttonColor: "primary",
                        method: emitter,
                    },
                    {
                        text: "Cancel",
                        method: "close",
                        styleClass: "btn-cancel",
                    },
                ],
            },
            disableClose: false,
        };
    }

    private setupSuspendDialog(emitter: EventEmitter<void>): IDialogConfig {
        return {
            id: "suspend-dealer",
            data: {
                title: "Suspend Account",
                message: AccountStatusMessages.suspendConfirmationMsg,
                loading: false,
                icon: "exclamation",
                isCloseShown: true,
                buttons: [
                    {
                        text: "Suspend",
                        buttonColor: "primary",
                        method: emitter,
                    },
                    {
                        text: "Cancel",
                        method: "close",
                        styleClass: "btn-cancel",
                    },
                ],
            },
            disableClose: false,
        };
    }

    private checkIfCanSuspend(projectListItem: ProjectListItem, config: IDialogConfig) {
        config.data.loading = true;
        config.data.loadingMessage = AccountStatusMessages.checkingActivationStatusMsg;
        this.servicePlanService
            .getActiveSubscriptionAndServicePlans(projectListItem?.dbData.id)
            .pipe(
                takeUntil(this.unsubscribe$),
                switchMap(({ servicePlan, subscription }) => this.setBasicServicePlanIfNeeded$(servicePlan, subscription, projectListItem)),
                take(1)
            )
            .subscribe();
    }

    private setBasicServicePlanIfNeeded$(
        currentServicePlan: ClareServicePlan,
        currentSubscription: ClareServiceSubscription,
        projectListItem: ProjectListItem
    ) {
        if (currentServicePlan?.name === ServicePlanTiers.BASIC) {
            // If we're already on Basic, nothing to downgrade to
            this.onActivateOrSuspend(projectListItem);
            return of(null);
        }

        return combineLatest([
            this.servicePlanService.getActiveServicePlans(projectListItem.dbData.grandfathered),
            this.chServiceService.getSecurityPanel(projectListItem.dbData.uuid).pipe(map(chdevice => (chdevice ? chdevice[0] : null))),
            this.projectDbService.getProjectAndRelated$(projectListItem.dbData.id),
        ]).pipe(
            take(1),
            switchMap(([servicePlans, securityDevice, chProject]) => {
                // const dbSecurityDevice = SecuritySystem.fromDbObject(chProject.securitysystem || new SecuritySystem(null));
                const dbSecurityDevice = chProject.securitysystem || new SecuritySystem(null);
                const cellDevice = this.cellularService.getCellularDeviceProperties(securityDevice, dbSecurityDevice);

                const newTier = chProject.grandfathered ? ServicePlanTiers.INTERACTIVE : ServicePlanTiers.BASIC;
                const newPlan = servicePlans.find(plan => plan.name === newTier);
                return this.cellularService.fetchCellularDevice(cellDevice?.iccid, cellDevice?.provider).pipe(
                    take(1),
                    tap(cellularDevice => {
                        if (currentServicePlan?.name !== newTier) {
                            // @TODO Equator should be notified when service plan changes. Is this sending that updatePermissions request?
                            const onChangePlanDone = new EventEmitter();
                            onChangePlanDone.pipe(take(1)).subscribe(() => {
                                this.onActivateOrSuspend(projectListItem);
                                this.refresh();
                            });
                            this.servicePlanService.savePlans(
                                currentSubscription,
                                currentServicePlan,
                                newPlan,
                                chProject,
                                cellularDevice,
                                onChangePlanDone,
                                [],
                                true,
                                true
                            );
                        } else {
                            this.onActivateOrSuspend(projectListItem);
                        }
                    })
                );
            })
        );
    }

    private getUserPermissions() {
        this.permissionsService.permissions$
            .pipe(
                tap((permissions: NgxPermissionsObject) => {
                    this.deleteActivatedProjectPermission = permissions["deleteProject"];
                    this.suspendActivatedProjectPermission = permissions["viewArchivedProjects"];
                })
            )
            .subscribe();
    }

    openConfirmDialog(config) {
        return this.dialog
            .open(ClareDialogComponent, {
                ...config,
                panelClass: "action-confirm-dialog",
                maxWidth: "477px",
            })
            .afterClosed()
            .pipe(takeUntil(this.unsubscribeFromDialog$))
            .subscribe(() => this.unsubscribeFromDialog());
    }

    activateProject(project: ProjectListItem) {
        const emitter = new EventEmitter();
        const config = this.setupActivateDialog(emitter);
        emitter.pipe(takeUntil(this.unsubscribeFromDialog$)).subscribe(() => this.onActivateOrSuspend(project));

        return this.openConfirmDialog(config);
    }

    suspendProject(project: ProjectListItem) {
        const emitter = new EventEmitter();
        const config = this.setupSuspendDialog(emitter);
        emitter.pipe(takeUntil(this.unsubscribeFromDialog$)).subscribe(() => this.checkIfCanSuspend(project, config));

        return this.openConfirmDialog(config);
    }

    getTableActions() {
        return (selectedRow): TableAction[] => {
            const actionList: TableAction[] = [];

            if (selectedRow) {
                if (selectedRow.uuid) {
                    if (selectedRow.inactive) {
                        actionList.push(TablePropertiesService.implementActivate(() => this.activateProject(selectedRow)));
                        if (this.isAbleToDeleteActiveProject) {
                            actionList.push(TablePropertiesService.implementDelete(() => this.deleteActivatedProject(selectedRow)));
                        }
                    } else if (this.isAbleToSuspendAccount) {
                        actionList.push(TablePropertiesService.implementSuspend(() => this.suspendProject(selectedRow)));
                    }
                } else {
                    actionList.push(TablePropertiesService.implementDelete(() => this.deleteProject(selectedRow)));
                }
            }

            return actionList;
        };
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.projectDispatcher.clearProjects();
        this.entityService.clearEntityState();
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    /**
     * Check if a value is of type T
     *
     * @param value
     * @param whitelist
     */
    isWhitelisted<T>(value: any, whitelist: T[]): value is T {
        return whitelist.includes(value);
    }

    /**
     * Extract all values of type T
     *
     * @param target
     * @param whitelist
     * @returns two lists, one containing all the Ts, and the other containing everything else
     */
    filterByWhitelist<T, V = any>(target: Array<T | V>, whitelist: T[]): [T[], V[]] {
        const valid: T[] = [];
        const invalid: V[] = [];
        target.forEach(x => {
            if (this.isWhitelisted(x, whitelist)) {
                valid.push(x);
            } else {
                invalid.push(x);
            }
        });
        return [valid, invalid];
    }
}
